import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../config';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { situacaoExameLabel } from '../utils/situacaoExame';
import './PainelGestor.css';
import './DataPage.css';

function getDateStr(date) {
  const d = date || new Date();
  return d.toISOString().split('T')[0];
}

function get30DaysAgoStr() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return getDateStr(d);
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#f97316', '#ec4899', '#6366f1', '#84cc16', '#06b6d4', '#e11d48', '#a855f7', '#22c55e', '#eab308'];

const formatNumber = (num) => {
  if (num === null || num === undefined) return '0';
  return Number(num).toLocaleString('pt-BR');
};

const formatCurrency = (num) => {
  if (num === null || num === undefined) return 'R$ 0,00';
  return Number(num).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (num) => {
  if (num === null || num === undefined) return '0%';
  return `${Number(num).toFixed(1)}%`;
};


function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

function DrillDownModal({ drillDown, onClose }) {
  if (!drillDown) return null;

  const { loading, titulo, dados, total, error } = drillDown;

  const colunas = dados && dados.length > 0 ? Object.keys(dados[0]) : [];

  const colLabels = {
    LOCAL: 'Local', PROTOCOLO: 'Protocolo', DATA: 'Data', HORARIO: 'Horario',
    PACIENTE: 'Paciente', CONVENIO: 'Convenio', VALOR: 'Valor', USUARIO: 'Usuario',
    EXAME: 'Exame', EXAME_NOME: 'Descricao', STATUS: 'Status', PLANO: 'Plano'
  };

  const formatCell = (col, val) => {
    if (val === null || val === undefined) return '-';
    if (col === 'VALOR') return formatCurrency(val);
    if (col === 'DATA' && typeof val === 'string' && val.includes('-')) return val.split('-').reverse().join('/');
    if (col === 'STATUS') return situacaoExameLabel(val);
    return String(val);
  };

  return (
    <div className="drilldown-overlay" onClick={onClose}>
      <div className="drilldown-modal" onClick={(e) => e.stopPropagation()}>
        <div className="drilldown-header">
          <div>
            <h2>{titulo || 'Detalhamento'}</h2>
            {total !== undefined && <span className="drilldown-count">{total} registro{total !== 1 ? 's' : ''}</span>}
          </div>
          <button className="drilldown-close" onClick={onClose}>&times;</button>
        </div>
        <div className="drilldown-body">
          {loading && (
            <div className="drilldown-loading">
              <div className="loading-spinner"></div>
              <p>Carregando detalhes...</p>
            </div>
          )}
          {error && <div className="gestao-error">{error}</div>}
          {!loading && !error && dados && dados.length === 0 && (
            <p className="no-data">Nenhum registro encontrado</p>
          )}
          {!loading && !error && dados && dados.length > 0 && (
            <table className="drilldown-table">
              <thead>
                <tr>
                  {colunas.map(col => (
                    <th key={col}>{colLabels[col] || col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dados.map((row, i) => (
                  <tr key={i}>
                    {colunas.map(col => (
                      <td key={col} className={col === 'VALOR' ? 'text-right' : ''}>{formatCell(col, row[col])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  pageIcon: {
    fontSize: '1.5rem',
    lineHeight: 1,
  },
  pageTitle: {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#1a1a2e',
    margin: '0 0 2px 0',
  },
  pageSubtitle: {
    fontSize: '0.85rem',
    color: '#666',
    margin: 0,
  },
};

function PainelGestor() {
  const isMobile = useIsMobile();
  const [dataInicio, setDataInicio] = useState(get30DaysAgoStr());
  const [dataFim, setDataFim] = useState(getDateStr());
  const [localSelecionados, setLocalSelecionados] = useState([]);
  const [convenioSelecionado, setConvenioSelecionado] = useState('');
  const [cidadeSelecionada, setCidadeSelecionada] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroOptions, setFiltroOptions] = useState({ locais: [], convenios: [], cidades: [] });

  const [drillDown, setDrillDown] = useState(null);
  const [hiddenSeries, setHiddenSeries] = useState({});

  const fetchFiltros = useCallback(async (inicio, fim) => {
    try {
      const params = new URLSearchParams();
      if (inicio) params.append('dataInicio', inicio);
      if (fim) params.append('dataFim', fim);
      const res = await axios.get(`${API_URL}/dashboard/gestao/filtros?${params.toString()}`);
      setFiltroOptions(res.data);
    } catch (err) {
      console.error('Erro ao buscar filtros:', err);
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (localSelecionados.length) params.append('local', localSelecionados.join(','));
      if (convenioSelecionado) params.append('convenio', convenioSelecionado);
      if (cidadeSelecionada) params.append('cidade', cidadeSelecionada);

      const [res] = await Promise.all([
        axios.get(`${API_URL}/dashboard/gestao?${params.toString()}`),
        fetchFiltros(dataInicio, dataFim)
      ]);
      setDados(res.data);
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError('Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [dataInicio, dataFim, localSelecionados, convenioSelecionado, cidadeSelecionada, fetchFiltros]);

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrillDown = useCallback(async (type, value) => {
    setDrillDown({ loading: true, titulo: '', dados: [], total: 0, error: '' });
    try {
      const params = new URLSearchParams();
      params.append('type', type);
      if (value !== undefined && value !== null) params.append('value', String(value));
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (localSelecionados.length) params.append('local', localSelecionados.join(','));
      if (convenioSelecionado) params.append('convenio', convenioSelecionado);
      if (cidadeSelecionada) params.append('cidade', cidadeSelecionada);
      const hk = Object.keys(hiddenSeries).filter(k => hiddenSeries[k]);
      if (hk.length) params.append('hidden', hk.join(','));

      const res = await axios.get(`${API_URL}/dashboard/gestao/drilldown?${params.toString()}`);
      setDrillDown({ loading: false, titulo: res.data.titulo, dados: res.data.dados, total: res.data.total, error: '' });
    } catch (err) {
      console.error('Erro no drilldown:', err);
      setDrillDown(prev => ({ ...prev, loading: false, error: 'Erro ao carregar detalhes.' }));
    }
  }, [dataInicio, dataFim, localSelecionados, convenioSelecionado, cidadeSelecionada, hiddenSeries]);

  const closeDrillDown = () => setDrillDown(null);

  // --- Legendas clicáveis: helpers ---
  const toggleSeries = (key) => {
    if (!key) return;
    setHiddenSeries(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderLegend = (props) => {
    const { payload } = props;
    return (
      <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '12px', padding: '4px 0' }}>
        {payload.map((entry, i) => {
          const key = entry.dataKey || entry.value;
          const off = hiddenSeries[key];
          return (
            <span key={i} onClick={() => toggleSeries(key)}
              style={{ cursor: 'pointer', opacity: off ? 0.35 : 1, textDecoration: off ? 'line-through' : 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', userSelect: 'none' }}>
              <span style={{ width: 10, height: 10, background: entry.color, borderRadius: 2, display: 'inline-block' }} />
              {entry.value}
            </span>
          );
        })}
      </div>
    );
  };

  const pieFilter = (data, nameKey) => data.map((e, i) => ({ ...e, _ci: i })).filter(e => !hiddenSeries[e[nameKey]]);

  const pieLegend = (data, nameKey) => () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', flexWrap: 'wrap', fontSize: '12px', padding: '4px 0' }}>
      {data.map((entry, i) => {
        const name = entry[nameKey];
        const off = hiddenSeries[name];
        return (
          <span key={i} onClick={() => toggleSeries(name)}
            style={{ cursor: 'pointer', opacity: off ? 0.35 : 1, textDecoration: off ? 'line-through' : 'none', display: 'inline-flex', alignItems: 'center', gap: '4px', userSelect: 'none' }}>
            <span style={{ width: 10, height: 10, background: COLORS[i % COLORS.length], borderRadius: 2, display: 'inline-block' }} />
            {name}
          </span>
        );
      })}
    </div>
  );

  const resetFiltros = () => {
    setDataInicio(get30DaysAgoStr());
    setDataFim(getDateStr());
    setLocalSelecionados([]);
    setConvenioSelecionado('');
    setCidadeSelecionada('');
  };

  const toggleLocal = (id) => {
    setLocalSelecionados(prev =>
      prev.includes(id) ? prev.filter(l => l !== id) : [...prev, id]
    );
  };

  return (
    <div className="gestao-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>🎯</span>
          <div>
            <h1 style={styles.pageTitle}>Painel do Gestor</h1>
            <p style={styles.pageSubtitle}>Visão gerencial completa do laboratório</p>
          </div>
        </div>
      </div>

      <div className={`gestao-filter-bar ${filterOpen ? 'filter-open' : ''}`}>
        <button className="filter-toggle-btn" onClick={() => setFilterOpen(!filterOpen)}>
          {filterOpen ? 'Ocultar Filtros' : 'Mostrar Filtros'}
          <span className={`filter-toggle-arrow ${filterOpen ? 'open' : ''}`}>&#9660;</span>
        </button>
        <div className={`filter-row ${filterOpen ? 'filter-visible' : ''}`}>
          <div className="filter-group">
            <label>Periodo</label>
            <div className="date-range">
              <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)} />
              <span className="date-separator">ate</span>
              <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)} />
            </div>
          </div>

          <div className="filter-group">
            <label>Local</label>
            <div className="local-checkboxes">
              {filtroOptions.locais.map(l => (
                <label key={l.ID} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={localSelecionados.includes(l.ID)}
                    onChange={() => toggleLocal(l.ID)}
                  />
                  <span>{l.DESCRICAO || l.ID}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="filter-group">
            <label>Convenio</label>
            <select value={convenioSelecionado} onChange={(e) => setConvenioSelecionado(e.target.value)}>
              <option value="">Todos</option>
              {filtroOptions.convenios.map(c => (
                <option key={c.IDENTIFICACAO} value={c.IDENTIFICACAO}>
                  {c.RAZAOSOCIAL || c.IDENTIFICACAO}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Cidade</label>
            <input
              type="text"
              list="cidades-datalist"
              value={cidadeSelecionada}
              onChange={(e) => setCidadeSelecionada(e.target.value)}
              placeholder="Buscar cidade..."
            />
            <datalist id="cidades-datalist">
              {filtroOptions.cidades.map(c => (
                <option key={c.CODUF || c.CIDADE} value={c.CIDADE}>{c.CIDADE} - {c.UF}</option>
              ))}
            </datalist>
          </div>

          <div className="filter-actions">
            <button className="btn-apply" onClick={() => { fetchData(); setFilterOpen(false); }}>Aplicar Filtros</button>
            <button className="btn-reset" onClick={() => { resetFiltros(); }}>Limpar</button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="gestao-loading">
          <div className="loading-spinner"></div>
          <p>Carregando dados...</p>
        </div>
      )}

      {error && <div className="gestao-error">{error}</div>}

      {dados && !loading && (
        <>
          <div className="compact-stats">
            <div className="compact-stat-card blue">
              <div className="compact-stat-value">{(dados.kpis?.totalAtendimentos || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Atendimentos</div>
            </div>
            <div className="compact-stat-card green">
              <div className="compact-stat-value">{(dados.kpis?.pacientesUnicos || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Pacientes Unicos</div>
            </div>
            <div className="compact-stat-card purple">
              <div className="compact-stat-value">{(dados.kpis?.totalExamesSolicitados || 0).toLocaleString('pt-BR')}</div>
              <div className="compact-stat-label">Exames Solicitados</div>
            </div>
            <div className="compact-stat-card orange">
              <div className="compact-stat-value">R$ {(dados.kpis?.ticketMedio || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
              <div className="compact-stat-label">Ticket Medio</div>
            </div>
            <div className="compact-stat-card teal">
              <div className="compact-stat-value">R$ {(dados.kpis?.valorTotal || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
              <div className="compact-stat-label">Valor Cobrado</div>
            </div>
            <div className="compact-stat-card green">
              <div className="compact-stat-value">R$ {(dados.kpis?.valorTotalPago || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
              <div className="compact-stat-label">Valor Pago</div>
            </div>
            <div className="compact-stat-card cyan">
              <div className="compact-stat-value">{(dados.kpis?.taxaConclusaoExames || 0).toFixed(1)}%</div>
              <div className="compact-stat-label">Taxa Conclusao</div>
            </div>
            {(dados.kpis?.pendentesColhidos > 0 || dados.kpis?.pendentesSemColeta > 0) && (
              <>
                <div className="compact-stat-card" style={{ borderLeft: '3px solid #f59e0b' }}>
                  <div className="compact-stat-value" style={{ color: '#d97706' }}>{(dados.kpis?.pendentesColhidos || 0).toLocaleString('pt-BR')}</div>
                  <div className="compact-stat-label">Pend. c/ Coleta</div>
                </div>
                <div className="compact-stat-card" style={{ borderLeft: '3px solid #ef4444' }}>
                  <div className="compact-stat-value" style={{ color: '#dc2626' }}>{(dados.kpis?.pendentesSemColeta || 0).toLocaleString('pt-BR')}</div>
                  <div className="compact-stat-label">Pend. s/ Coleta</div>
                </div>
              </>
            )}
          </div>

          <div className="gestao-charts-grid">
            {/* Section: Convenios */}
            <div className="gestao-section-header">Analise de Convenios</div>

            {/* PieChart: Tipo de Convenio */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Atendimentos por Tipo de Convenio <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.atendimentosPorTipoConvenio?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <PieChart>
                    <Pie
                      data={pieFilter(dados.atendimentosPorTipoConvenio, 'tipo_convenio')}
                      dataKey="total"
                      nameKey="tipo_convenio"
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 60 : 100}
                      label={isMobile ? false : ({ tipo_convenio, percentual }) => `${tipo_convenio} (${(percentual || 0).toFixed(1)}%)`}
                      onClick={(data) => handleDrillDown('tipoConvenio', data.tipo_convenio)}
                      style={{ cursor: 'pointer' }}
                    >
                      {pieFilter(dados.atendimentosPorTipoConvenio, 'tipo_convenio').map((entry) => (
                        <Cell key={entry._ci} fill={COLORS[entry._ci % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend content={pieLegend(dados.atendimentosPorTipoConvenio, 'tipo_convenio')} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* BarChart Horizontal: Top 15 Convenios */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Top 15 Convenios <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.atendimentosPorConvenio?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
                  <BarChart data={dados.atendimentosPorConvenio} layout="vertical" margin={{ left: isMobile ? 0 : 20 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="convenio_nome" width={isMobile ? 80 : 140} tick={{ fontSize: isMobile ? 9 : 11 }} />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="total" fill="#3b82f6" name="Atendimentos" radius={[0, 4, 4, 0]}
                      onClick={(data) => handleDrillDown('convenio', data.convenio_nome)}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* PieChart: Pagamentos por Forma */}
            <div className="gestao-chart-card">
              <h3>Pagamentos por Forma</h3>
              {dados.pagamentosPorForma?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <PieChart>
                    <Pie
                      data={dados.pagamentosPorForma.map((entry, i) => ({ ...entry, _ci: i }))}
                      dataKey="valor_total"
                      nameKey="forma_pagamento"
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 60 : 100}
                      label={isMobile ? false : ({ forma_pagamento, percentual }) => `${forma_pagamento} (${(percentual || 0).toFixed(1)}%)`}
                    >
                      {dados.pagamentosPorForma.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* Section: Evolucao */}
            <div className="gestao-section-header">Evolucao</div>

            {/* AreaChart: Evolucao Diaria (full width) */}
            <div className="gestao-chart-card full-width chart-clickable">
              <h3>Evolucao Diaria de Atendimentos <span className="drill-hint">Clique nos pontos para detalhes</span></h3>
              {dados.evolucaoDiaria?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                  <AreaChart data={dados.evolucaoDiaria} margin={isMobile ? { left: -10, right: 5 } : undefined}
                    onClick={(e) => { if (e?.activePayload?.[0]) handleDrillDown('evolucao', e.activePayload[0].payload.dia); }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia" tickFormatter={(v) => { const parts = v.split('-'); return `${parts[2]}/${parts[1]}`; }} tick={{ fontSize: isMobile ? 9 : 11 }} />
                    <YAxis tick={{ fontSize: isMobile ? 9 : 12 }} width={isMobile ? 30 : 60} />
                    <Tooltip labelFormatter={(v) => `Data: ${v.split('-').reverse().join('/')}`} formatter={(value) => formatNumber(value)} />
                    <Area type="monotone" dataKey="total" stroke="#3b82f6" fill="rgba(59, 130, 246, 0.3)" name="Atendimentos"
                      activeDot={{ r: 6, style: { cursor: 'pointer' } }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* Section: Distribuicao Temporal */}
            <div className="gestao-section-header">Distribuicao Temporal</div>

            {/* BarChart: Por Hora do Dia */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Atendimentos por Hora do Dia <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.atendimentosPorHora?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                  <BarChart data={dados.atendimentosPorHora} margin={isMobile ? { left: -10, right: 5 } : undefined}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hora" tickFormatter={(v) => `${v}h`} tick={{ fontSize: isMobile ? 9 : 12 }} />
                    <YAxis tick={{ fontSize: isMobile ? 9 : 12 }} width={isMobile ? 30 : 60} />
                    <Tooltip labelFormatter={(v) => `${v}:00 - ${v}:59`} formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="total" fill="#8b5cf6" name="Atendimentos" radius={[4, 4, 0, 0]}
                      onClick={(data) => handleDrillDown('hora', data.hora)}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* BarChart: Por Dia da Semana */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Atendimentos por Dia da Semana <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.atendimentosPorDiaSemana?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                  <BarChart data={dados.atendimentosPorDiaSemana} margin={isMobile ? { left: -10, right: 5 } : undefined}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="dia_semana" tick={{ fontSize: isMobile ? 9 : 11 }} />
                    <YAxis tick={{ fontSize: isMobile ? 9 : 12 }} width={isMobile ? 30 : 60} />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="total" fill="#10b981" name="Atendimentos" radius={[4, 4, 0, 0]}
                      onClick={(data) => handleDrillDown('diaSemana', data.dia_num)}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* Section: Unidades e Geografia */}
            <div className="gestao-section-header">Unidades e Geografia</div>

            {/* BarChart: Por Local */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Atendimentos por Unidade <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.atendimentosPorLocal?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 220 : 300}>
                  <BarChart data={dados.atendimentosPorLocal} margin={isMobile ? { left: -10, right: 5 } : undefined}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="local_nome" tick={{ fontSize: isMobile ? 9 : 11 }} />
                    <YAxis tick={{ fontSize: isMobile ? 9 : 12 }} width={isMobile ? 30 : 60} />
                    <Tooltip formatter={(value, name) => {
                      const labels = { valor_total: 'Valor Cobrado', valor_total_pago: 'Valor Pago', total_atendimentos: 'Atendimentos', total_pacientes: 'Pacientes' };
                      const isCurrency = name === 'valor_total' || name === 'valor_total_pago';
                      return [isCurrency ? formatCurrency(value) : formatNumber(value), labels[name] || name];
                    }} />
                    <Legend content={renderLegend} />
                    <Bar dataKey="total_atendimentos" fill="#3b82f6" name="Atendimentos" radius={[4, 4, 0, 0]}
                      hide={!!hiddenSeries['total_atendimentos']}
                      onClick={(data) => handleDrillDown('local', data.local_id)}
                      style={{ cursor: 'pointer' }}
                    />
                    <Bar dataKey="total_pacientes" fill="#10b981" name="Pacientes" radius={[4, 4, 0, 0]} hide={!!hiddenSeries['total_pacientes']} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* BarChart Horizontal: Top 15 Cidades */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Top 15 Cidades <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.distribuicaoPorCidade?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
                  <BarChart data={dados.distribuicaoPorCidade} layout="vertical" margin={{ left: isMobile ? 0 : 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="cidade" width={isMobile ? 80 : 130} tick={{ fontSize: isMobile ? 9 : 11 }} />
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="total_atendimentos" fill="#f59e0b" name="Atendimentos" radius={[0, 4, 4, 0]}
                      onClick={(data) => handleDrillDown('cidade', data.cidade)}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* Section: Exames */}
            <div className="gestao-section-header">Exames</div>

            {/* BarChart: Aging de Pendentes por Dia (full width) */}
            <div className="gestao-chart-card full-width chart-clickable">
              <h3>Exames Pendentes por Dia (Aging) <span className="drill-hint">Clique para detalhes</span></h3>
              <p className="aging-subtitle">Pendentes agrupados por data de solicitacao. <strong style={{ color: '#d97706' }}>Laranja</strong> = material colhido (aguardando lab). <strong style={{ color: '#ef4444' }}>Vermelho</strong> = sem coleta (aguardando paciente/coleta).</p>
              {dados.pendentesPorDia?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 320}>
                  <BarChart data={dados.pendentesPorDia} margin={{ top: 10, right: 5, bottom: 20, left: isMobile ? -10 : 10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="data_solicitacao"
                      tickFormatter={(v) => { const parts = v.split('-'); return `${parts[2]}/${parts[1]}`; }}
                      tick={{ fontSize: 10 }}
                      angle={-45}
                      textAnchor="end"
                      height={50}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      labelFormatter={(v) => {
                        const item = dados.pendentesPorDia.find(d => d.data_solicitacao === v);
                        const dataFormatada = v.split('-').reverse().join('/');
                        return item ? `${dataFormatada} (${item.dias_retroativos} dia${item.dias_retroativos !== 1 ? 's' : ''} atras)` : dataFormatada;
                      }}
                      formatter={(value, name) => [formatNumber(value), name]}
                    />
                    <Legend content={renderLegend} />
                    <Bar dataKey="pendentes_colhidos" name="Colhido (aguard. lab)" stackId="pend" fill="#d97706"
                      hide={!!hiddenSeries['pendentes_colhidos']}
                      onClick={(data) => handleDrillDown('pendentesAgingDia', data.data_solicitacao)}
                      style={{ cursor: 'pointer' }}
                    />
                    <Bar dataKey="pendentes_sem_coleta" name="Sem coleta" stackId="pend" fill="#ef4444" radius={[4, 4, 0, 0]}
                      hide={!!hiddenSeries['pendentes_sem_coleta']}
                      onClick={(data) => handleDrillDown('pendentesAgingDia', data.data_solicitacao)}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Nenhum exame pendente no periodo</p>}
            </div>

            {/* Table: Top 15 Exames with progress bars (full width) */}
            <div className="gestao-chart-card full-width">
              <h3>Top 15 Exames Mais Solicitados <span className="drill-hint">Clique na linha para detalhes</span></h3>
              {dados.topExames?.length > 0 ? (
                <div className="gestao-table-container">
                  <table className="gestao-table table-clickable">
                    <thead>
                      <tr>
                        <th>Codigo</th>
                        <th>Descricao</th>
                        <th>Total</th>
                        <th>Concluidos</th>
                        <th>Pend. c/ Coleta</th>
                        <th>Pend. s/ Coleta</th>
                        <th>Conclusao</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.topExames.map((exame, i) => {
                        const pct = exame.total_solicitacoes > 0 ? (exame.concluidos / exame.total_solicitacoes) * 100 : 0;
                        return (
                          <tr key={i} onClick={() => handleDrillDown('exame', exame.codigo)} style={{ cursor: 'pointer' }}>
                            <td><code>{exame.codigo}</code></td>
                            <td>{exame.descricao || exame.codigo}</td>
                            <td className="text-center">{formatNumber(exame.total_solicitacoes)}</td>
                            <td className="text-center" style={{ color: '#10b981' }}>{formatNumber(exame.concluidos)}</td>
                            <td className="text-center" style={{ color: exame.pendentes_colhidos > 0 ? '#d97706' : '#6b7280' }}>{formatNumber(exame.pendentes_colhidos || 0)}</td>
                            <td className="text-center" style={{ color: exame.pendentes_sem_coleta > 0 ? '#dc2626' : '#6b7280' }}>{formatNumber(exame.pendentes_sem_coleta || 0)}</td>
                            <td>
                              <div className="progress-bar-container">
                                <div className="progress-bar-fill" style={{ width: `${pct}%`, backgroundColor: pct >= 90 ? '#10b981' : pct >= 70 ? '#f59e0b' : '#ef4444' }}></div>
                                <span className="progress-bar-text">{pct.toFixed(1)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* BarChart: Exames Pendentes */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Top 20 Exames Pendentes <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.examesPendentes?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
                  <BarChart data={dados.examesPendentes} layout="vertical" margin={{ left: isMobile ? 0 : 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="codigo" width={isMobile ? 60 : 100} tick={{ fontSize: isMobile ? 8 : 10 }} />
                    <Tooltip formatter={(value, name) => [formatNumber(value), name]} labelFormatter={(v) => {
                      const item = dados.examesPendentes.find(e => e.codigo === v);
                      return item?.descricao || v;
                    }} />
                    <Legend content={renderLegend} />
                    <Bar dataKey="pendentes_colhidos" name="Colhido (aguard. lab)" stackId="pend" fill="#d97706"
                      hide={!!hiddenSeries['pendentes_colhidos']}
                      onClick={(data) => handleDrillDown('examePendente', data.codigo)}
                      style={{ cursor: 'pointer' }}
                    />
                    <Bar dataKey="pendentes_sem_coleta" name="Sem coleta" stackId="pend" fill="#ef4444" radius={[0, 4, 4, 0]}
                      hide={!!hiddenSeries['pendentes_sem_coleta']}
                      onClick={(data) => handleDrillDown('examePendente', data.codigo)}
                      style={{ cursor: 'pointer' }}
                    />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* PieChart: Faixa Etaria */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Distribuicao por Faixa Etaria <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.faixaEtaria?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
                  <PieChart>
                    <Pie
                      data={pieFilter(dados.faixaEtaria, 'faixa_etaria')}
                      dataKey="total"
                      nameKey="faixa_etaria"
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 60 : 100}
                      label={isMobile ? false : ({ faixa_etaria, percent }) => `${faixa_etaria} (${(percent * 100).toFixed(1)}%)`}
                      onClick={(data) => handleDrillDown('faixaEtaria', data.faixa_etaria)}
                      style={{ cursor: 'pointer' }}
                    >
                      {pieFilter(dados.faixaEtaria, 'faixa_etaria').map((entry) => (
                        <Cell key={entry._ci} fill={COLORS[entry._ci % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                    <Legend content={pieLegend(dados.faixaEtaria, 'faixa_etaria')} />
                  </PieChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* Section: Produtividade e Financeiro */}
            <div className="gestao-section-header">Produtividade e Financeiro</div>

            {/* BarChart Horizontal: Produtividade por Usuario */}
            <div className="gestao-chart-card chart-clickable">
              <h3>Produtividade por Usuario <span className="drill-hint">Clique para detalhes</span></h3>
              {dados.produtividadePorUsuario?.length > 0 ? (
                <ResponsiveContainer width="100%" height={isMobile ? 300 : 350}>
                  <BarChart data={dados.produtividadePorUsuario} layout="vertical" margin={{ left: isMobile ? 0 : 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis type="category" dataKey="usuario" width={isMobile ? 70 : 120} tick={{ fontSize: isMobile ? 9 : 11 }} />
                    <Tooltip formatter={(value, name) => [formatNumber(value), name === 'total_atendimentos' ? 'Atendimentos' : 'Pacientes']} />
                    <Legend content={renderLegend} />
                    <Bar dataKey="total_atendimentos" fill="#3b82f6" name="Atendimentos" radius={[0, 4, 4, 0]}
                      hide={!!hiddenSeries['total_atendimentos']}
                      onClick={(data) => handleDrillDown('usuario', data.usuario)}
                      style={{ cursor: 'pointer' }}
                    />
                    <Bar dataKey="total_pacientes" fill="#10b981" name="Pacientes" radius={[0, 4, 4, 0]} hide={!!hiddenSeries['total_pacientes']} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <p className="no-data">Sem dados</p>}
            </div>

            {/* Table: Faturamento por Convenio */}
            <div className="gestao-chart-card">
              <h3>Faturamento por Convenio <span className="drill-hint">Clique na linha para detalhes</span></h3>
              {dados.faturamentoResumo?.length > 0 ? (
                <div className="gestao-table-container">
                  <table className="gestao-table table-clickable">
                    <thead>
                      <tr>
                        <th>Convenio</th>
                        <th className="text-right">Protocolos</th>
                        <th className="text-right">Itens</th>
                        <th className="text-right">Valor Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.faturamentoResumo.map((fat, i) => (
                        <tr key={i} onClick={() => handleDrillDown('faturamento', fat.convenio)} style={{ cursor: 'pointer' }}>
                          <td>{fat.convenio || 'Nao informado'}</td>
                          <td className="text-right">{formatNumber(fat.total_protocolos)}</td>
                          <td className="text-right">{formatNumber(fat.total_itens)}</td>
                          <td className="text-right" style={{ fontWeight: 600 }}>{formatCurrency(fat.valor_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td><strong>Total</strong></td>
                        <td className="text-right"><strong>{formatNumber(dados.faturamentoResumo.reduce((s, f) => s + (f.total_protocolos || 0), 0))}</strong></td>
                        <td className="text-right"><strong>{formatNumber(dados.faturamentoResumo.reduce((s, f) => s + (f.total_itens || 0), 0))}</strong></td>
                        <td className="text-right"><strong>{formatCurrency(dados.faturamentoResumo.reduce((s, f) => s + (f.valor_total || 0), 0))}</strong></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : <p className="no-data">Sem dados</p>}
            </div>
          </div>
        </>
      )}

      <DrillDownModal drillDown={drillDown} onClose={closeDrillDown} />
    </div>
  );
}

export default PainelGestor;
